/**
 * Utility to sync localStorage credentials to user account
 * Called when user logs in to ensure all local credentials are backed up
 */

import { useBooruCredentialsStore } from './booru-credentials-store'
import { useSourceStore } from './source-store'

export async function syncAllCredentialsToAccount() {
  try {
    const credStore = useBooruCredentialsStore.getState()
    const sourceStore = useSourceStore.getState()
    const allCredentials = credStore.credentials

    if (!allCredentials || Object.keys(allCredentials).length === 0) {
      console.log('[v0] No localStorage credentials to sync')
      return
    }

    // Get enabled sources
    const enabledSources = sourceStore.getEnabledSources()
    const enabledSourceIds = new Set(enabledSources.map(s => s.id))

    // Filter credentials to only include enabled sources
    const credentialsToSync = Object.entries(allCredentials).filter(([booruId]) => 
      enabledSourceIds.has(booruId)
    )

    if (credentialsToSync.length === 0) {
      console.log('[v0] No credentials for enabled sources to sync')
      return
    }

    console.log('[v0] Syncing', credentialsToSync.length, 'credentials to account (from', Object.keys(allCredentials).length, 'total)')

    // Sync each credential
    const syncPromises = credentialsToSync.map(async ([booruId, creds]) => {
      if (!creds.apiKey) return // Skip if no API key

      try {
        // Check if account already has credentials for this booru
        const checkResponse = await fetch(`/api/booru/credentials?booruId=${booruId}`)
        if (checkResponse.ok) {
          const data = await checkResponse.json()
          if (data.credentials?.apiKey) {
            console.log('[v0] Account already has credentials for', booruId, '- skipping')
            return
          }
        }

        // Sync to account
        const response = await fetch('/api/booru/credentials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            booruId,
            username: creds.username,
            apiKey: creds.apiKey,
          }),
        })

        if (response.ok) {
          console.log('[v0] Synced credentials for', booruId, 'to account')
        } else {
          console.error('[v0] Failed to sync credentials for', booruId)
        }
      } catch (error) {
        console.error('[v0] Error syncing credentials for', booruId, ':', error)
      }
    })

    await Promise.all(syncPromises)
    console.log('[v0] Credential sync complete')
  } catch (error) {
    console.error('[v0] Error in syncAllCredentialsToAccount:', error)
  }
}

export async function loadAllCredentialsFromAccount() {
  try {
    // This would require a new API endpoint to get all credentials at once
    // For now, credentials are loaded individually when auth dialog opens
    console.log('[v0] Individual credential loading happens per-source')
  } catch (error) {
    console.error('[v0] Error loading credentials from account:', error)
  }
}
